import { useMemo, useEffect } from "react"
import { Form } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { getTrips } from "store/boats"
import dayjs from "dayjs"
import { DISCOUNT_TYPE } from "./constants"

export const useDiscountFormData = ({ form, visible, initialValues }) => {
  const dispatch = useDispatch()
  const tripsState = useSelector((state) => state?.trips?.items || [])
  const selectedIds = Form.useWatch("applicable_experiences", form) ?? []

  useEffect(() => {
    if (tripsState.length === 0) dispatch(getTrips())
  }, [dispatch])

  const experiences = useMemo(
    () =>
      tripsState.map((trip) => ({
        id: String(trip.id),
        name: trip.title || "Untitled",
        status: trip.status,
        guests: trip.max_guests || 0,
        price: trip.min_price ? Number(trip.min_price) : 0
      })),
    [tripsState]
  )

  const selectedExperienceData = useMemo(() => {
    const selectedSet = new Set(selectedIds.map(String))
    return experiences.filter((exp) => selectedSet.has(String(exp.id)))
  }, [experiences, selectedIds])

  useEffect(() => {
    if (visible && initialValues) {
      form.setFieldsValue({
        name: initialValues.name,
        code: initialValues.code,
        discount_type: initialValues.discount_type ?? DISCOUNT_TYPE.PERCENTAGE,
        value: initialValues.value,
        applicable_experiences:
          initialValues.applicable_experiences === "all"
            ? experiences.map((e) => e.id)
            : initialValues.applicable_experiences ?? [],
        validity:
          initialValues.valid_from || initialValues.valid_until
            ? [
                initialValues.valid_from ? dayjs(initialValues.valid_from) : null,
                initialValues.valid_until ? dayjs(initialValues.valid_until) : null
              ]
            : null,
        usage_cap: initialValues.usage_cap === "unlimited" ? null : initialValues.usage_cap || null,
        per_user_usage_cap:
          initialValues.per_user_usage_cap === "unlimited" ? null : initialValues.per_user_usage_cap || null,
        status: initialValues.status ?? "active",
        apply_to_add_ons: initialValues.applicability_rules?.apply_to_add_ons ?? true
      })
      const fieldNames = form.getFieldsValue(true)
      form.setFields(Object.keys(fieldNames).map((name) => ({ name, touched: false })))
    } else if (visible && !initialValues) {
      form.resetFields()
      form.setFieldsValue({
        discount_type: DISCOUNT_TYPE.PERCENTAGE,
        status: "active",
        apply_to_add_ons: true
      })
      const fieldNames = form.getFieldsValue(true)
      form.setFields(Object.keys(fieldNames).map((name) => ({ name, touched: false })))
    }
  }, [visible, initialValues, experiences])

  const handleRemoveExperience = (expId) => {
    const next = selectedIds.filter((id) => String(id) !== String(expId))
    form.setFieldValue("applicable_experiences", next)
  }

  return { experiences, selectedExperienceData, handleRemoveExperience }
}
